import { useEffect, useState } from "react"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { IOrderListProjection } from "../../../types/order/IOrderListProjection"
import { useNavigate } from "react-router-dom"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { Header } from "../../common/header/Header"
import { OrderStatus } from "../../../types/order/OrderStatus"
import { IOrderStatus } from "../../../types/order/IOrderStatus"
import { getMskTime, round2Digits, spacedNum } from "../../common/CommonHelper"
import { MenuAdmin } from "../../admin/MenuAdmin"
import { useStore } from "../../../stores/RootStoreContext"
import { MenuManager } from "../MenuManager"
import { IPageResponse } from "../../../types/IPageResponse"
import { ICheckConversationResponse } from "../../../types/messenger/ICheckConversationResponse"
import { Messenger } from "../../common/messenger/Messenger"
import { IMG } from "../../common/CONST"
import { CONFIG } from "../../../CONFIG"
import Button from "@/ui/components/Button"
import Input from "@/ui/components/Input"
import Skeleton from "@/ui/components/Skeleton"
import EmptyRow from "@/ui/components/EmptyRow"
import AXIOS from "@/requests/_axios"
import { ModalConfirm } from "@/components/common/element/ModalConfirm"

export const ManagerOrderList = () => {
  const navigate = useNavigate()
  const { userStore } = useStore()

  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [confirmMessage, setConfirmMessage] = useState<IModalMessage>()
  const [orders, setOrders] = useState<IOrderListProjection[]>([])
  const [filteredOrders, setFilteredOrders] = useState<IOrderListProjection[]>([])
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("")
  const [isLoading, setLoading] = useState(true)
  const [isRefetchLoading, setRefetchLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  /* MESSENGER FCN */
  const [instanceId, setInstanceId] = useState<string>()
  const [conversationUserId, setConversationUserId] = useState<number>()
  const CHAT_INSTANCE = "ORDER"
  const [unreadList, setUnreadList] = useState<IPageResponse<ICheckConversationResponse>>()

  const fetchUnread = () => {
    TrinityAPI.getUnreadConversations(CHAT_INSTANCE, response => {
      if (response.status === 200) {
        setUnreadList(response.data)
      }
    })
  }

  const closeMessenger = () => {
    setInstanceId(undefined)
    setConversationUserId(undefined)
  }

  const showMessenger = (instanceId: string | undefined, userId: number | undefined) => {
    setInstanceId(instanceId)
    setConversationUserId(userId)
  }

  const getUnreadCounter = (instanceId: string | undefined): number => {
    let counter = 0
    if (!instanceId) return counter
    let unreadItem = unreadList?.items.filter(item => item.instanceId === instanceId)[0]
    counter = unreadItem ? unreadItem.num : 0
    return counter
  }

  /* EOF MESSENGER FCN */

  const fetchOrders = () => {
    setSearch("")

    TrinityAPI.getOrderList(
      status,
      response => {
        if (response.status === 200) {
          setOrders(response.data)
          setFilteredOrders(response.data)
        } else {
          setModalMessage({
            title: "Ошибка!",
            text: `Ошибка получения ордеров: ${response.data.message}`
          })
        }
      },
      () => {
        setLoading(false)
        setRefetchLoading(false)
      }
    )
  }

  const refetchData = () => {
    setRefetchLoading(true)
    fetchOrders()
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const response = await AXIOS.get("/trinity/api/v1/unload/xls/orders")

      if (response.status === 200) {
        const { data } = response

        const element = document.createElement("a")
        element.href = `data:application/vnd.ms-excel;base64,${response.data.fileBase64}`
        element.download = data.fileName
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
      }
    } catch (err) {
      setIsDownloading(false)
    }
    setIsDownloading(false)
  }

  const confirmRemove = (e: boolean | undefined, id: number) => {
    if (e) {
      remove(id)
    }
  }

  const remove = (id: number) => {
    TrinityAPI.deleteOrder(id, response => {
      if (response.status === 200) {
        fetchOrders()
      }
    })
  }

  useEffect(() => {
    fetchOrders()
  }, [status])

  useEffect(() => {
    if (!search) {
      setFilteredOrders(orders)
      return
    }

    setFilteredOrders(
      orders.filter(
        r =>
          (r.contractor && r.contractor.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.country && r.country.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.amount && round2Digits(Number(r.amount) / 100).toString() === search.toLocaleLowerCase()) ||
          (r.clientName && r.clientName.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.clientCompanyName && r.clientCompanyName.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.id && r.id.toString() === search) ||
          (r.currency && r.currency.toLocaleLowerCase() === search.toLocaleLowerCase())
      )
    )
  }, [search])

  useEffect(() => {
    fetchUnread()

    const intervalId = setInterval(() => {
      fetchUnread()
    }, CONFIG.fetchUnreadsInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return (
    <>
      <ModalConfirm message={confirmMessage} setMessage={() => setConfirmMessage(undefined)} />
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
      {userStore.isManager ? <MenuManager active={"order"} /> : <MenuAdmin active={"order"} />}
      <main className="main-container" id="scrollable">
        <Header />
        <div className="container">
          <h1 className="ui-heading">Ордера</h1>

          <section className="header-chapter">
            <div className="headr-chapter-name">
              <div className="header-chapter-icon">
                <img src={IMG.requests} alt="" className="header-chapter-img" />
              </div>
              <Button variant="black" loading={isRefetchLoading} onClick={refetchData}>
                Обновить список ордеров
              </Button>
            </div>
            <Button onClick={handleDownload} variant="black" loading={isDownloading}>
              Сверка ордеров
            </Button>
          </section>

          <section className="search-block">
            <Input
              type="search"
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по контрагенту, имени или компании клиента, стране, сумме или валюте"
            />
          </section>

          <div className="main-content--wrapper">
            <section className="main-content">
              <ul className="header-global-list">
                <li className="header-global-item width-130">Дата и номер</li>
                <li className="header-global-item width-130">Сумма</li>
                <li className="header-global-item width-100">Валюта</li>
                <li className="header-global-item cols-deals min-width-150">Получатель (Страна)</li>
                <li className="header-global-item cols-deals min-width-150">Плательщик (Страна)</li>
                <li className="header-global-item cols-deals" style={{ paddingLeft: "5px" }}>
                  Клиент
                </li>
                <li className="header-global-item width-200">
                  <select value={status} className="select-style" onChange={e => setStatus(e.target.value)}>
                    <option key="EMPTY" value="">
                      Статус
                    </option>
                    {Object.keys(OrderStatus).map((key, index) => (
                      <option value={key} key={index.toString()}>
                        {OrderStatus[key as keyof IOrderStatus][0]}
                      </option>
                    ))}
                  </select>
                </li>
                {userStore.isAdmin && <li className="header-global-item width-115 text-end">Действия</li>}
              </ul>
              <ul className="global-list">
                {isLoading && (
                  <div className="skeleton-wrapper">
                    <Skeleton rows={4} gutterTop />
                  </div>
                )}
                {!isLoading &&
                  (filteredOrders.length > 0 ? (
                    filteredOrders.map((o, index) => (
                      <li
                        className={`global-item${
                          o.status === "NEW" || o.status === "SWIFT" || o.status === "INQUIRY" ? "-active" : ""
                        }`}
                        key={o.id}
                      >
                        <div className="global-scroll-block" onClick={() => window.open("/order/" + o.id, "_blank")}>
                          <p className="global-item-text width-130">
                            <span>№{o.id}</span>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: getMskTime((o.status === "ARCHIVED" && o.doneDate ? o.doneDate : o.createdAt) || "") || ""
                              }}
                            />
                          </p>
                          <p className="global-item-text width-130">
                            {o.amount ? spacedNum(round2Digits(Number(o.amount) / 100)) : "-"}
                          </p>
                          <p className="global-item-text globak-num width-100">{o.currency || "-"}</p>
                          <p className="deal-item-flex cols-deals min-width-150">
                            <span className="deal-country">{o.country?.toUpperCase() || "-"}</span>
                            <span className="deal-name">{o.contractor || "-"}</span>
                          </p>
                          <p className="deal-item-flex cols-deals min-width-150">
                            <span className="deal-country">{o.companyCountryName?.toUpperCase()}</span>
                            <span className="deal-name">{o.companyName || "-"}</span>
                          </p>
                          <p className="deal-item-flex cols-deals breakWord">
                            <span className="deal-country">{o.clientName || "-"}</span>
                            <span className="deal-name">{o.clientCompanyName || "с баланса"}</span>
                          </p>
                          <p className={`status width-200`}>
                            {o.status ? OrderStatus[o.status as keyof IOrderStatus][1] : ""}
                          </p>
                        </div>
                        <button className="chat-deals" onClick={() => showMessenger(o.id?.toString(), o.userId)}>
                          {getUnreadCounter(o.id?.toString()) > 0 ? (
                            <span className="message-new">{getUnreadCounter(o.id?.toString())}</span>
                          ) : (
                            ""
                          )}
                        </button>
                        {userStore.isAdmin && (
                          <div className="header-global-item width-115 buttons-end">
                            <Button
                              variant="black"
                              onClick={() =>
                                setConfirmMessage({
                                  title: "Удалить ордер?",
                                  text: "Вы действительно желаете удалить ордер?",
                                  callbackValue: true,
                                  _callback: (val: any) => confirmRemove(val, o.id)
                                })
                              }
                            >
                              Удалить
                            </Button>
                          </div>
                        )}
                      </li>
                    ))
                  ) : (
                    <EmptyRow text="Ордеров не найдено" />
                  ))}
              </ul>
            </section>
          </div>
        </div>
        <Messenger instanceId={instanceId} type={CHAT_INSTANCE} userId={conversationUserId} close={closeMessenger} />
      </main>
    </>
  )
}
